import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiCallError, getDoorTextConfig, saveDoorTextConfig } from '@/api/client';
import type { DoorTextConfigDto, DoorTextConfigResponseDto } from '@/api/types';

/**
 * WHAT THE DOCK-DOOR TEXT SAYS, per facility (migration 147).
 *
 * ⭐ TWO PREVIEWS, ALWAYS. The message a driver is most likely to receive is the
 * one where there is NO door yet — he is in the lot, that is why he is being
 * texted. A screen that previewed only the happy path would let a site reword
 * itself into a message it never actually looks at.
 *
 * ⭐ THE PREVIEWS COME FROM THE SERVER, rendered by the same function that
 * composes the real thing. A local copy would drift, and a preview that lies is
 * worse than none — it is confidently wrong exactly when somebody is deciding
 * whether the wording is right.
 *
 * ⛔ THE ORDER OF THE BLOCKS IS NOT A SETTING. Door first, detail after (Pete,
 * 2026-08-19). Letting a site reorder them would let somebody bury the door
 * under three lines of detail, which is the one thing this message exists to
 * deliver.
 */

interface Props {
    getToken: () => Promise<string>;
    // ⚠️ CAPITALISED, matching `lookups.facilities` and every other admin tab.
    // A lower-case shape here type-checks in isolation and fails the moment
    // SettingsDialog passes the real thing — which is exactly what it did.
    facilities: { FacilityCode: string; DisplayName: string }[];
    /**
     * ⭐ THE FACILITY THE CALENDAR IS SHOWING. See DocumentConfigAdmin for what
     * defaulting to facilities[0] cost on 2026-08-25: settings saved against a
     * facility the user was not looking at, and no screen able to say so.
     */
    initialFacilityCode?: string | null;
}

const TOGGLES: { key: keyof DoorTextConfigDto; label: string; hint: string }[] = [
    { key: 'includeDoor', label: 'Dock door', hint: 'The line that says which door to back into.' },
    { key: 'includeAppointment', label: 'Appointment number', hint: 'So he can check it against his paperwork.' },
    { key: 'includeTruckNumber', label: 'Truck number', hint: "This client's Nth truck today." },
    { key: 'includeFacility', label: 'Facility name', hint: 'Useful for carriers running several sites.' },
    { key: 'includeSchedule', label: 'Appointment time', hint: "Always in this site's own timezone." },
    { key: 'includeQueue', label: 'How busy the door is', hint: 'Trucks ahead, and what the one at the door is doing.' },
];

export default function DoorTextConfigAdmin({ getToken, facilities, initialFacilityCode }: Props) {
    const [facilityCode, setFacilityCode] = useState(
        (initialFacilityCode && facilities.some(f => f.FacilityCode === initialFacilityCode)
            ? initialFacilityCode
            : facilities[0]?.FacilityCode) ?? '',
    );
    const [data, setData] = useState<DoorTextConfigResponseDto | null>(null);
    const [config, setConfig] = useState<DoorTextConfigDto | null>(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [saved, setSaved] = useState(false);

    const load = useCallback(async () => {
        if (!facilityCode) return;
        setLoading(true);
        setError(null);
        setSaved(false);
        try {
            const res = await getDoorTextConfig(await getToken(), facilityCode);
            setData(res);
            setConfig(res.config);
        } catch (err) {
            setError(err instanceof ApiCallError ? err.details.error : 'Could not load the wording.');
        } finally {
            setLoading(false);
        }
    }, [facilityCode, getToken]);

    useEffect(() => { void load(); }, [load]);

    /**
     * ⚠️ THE PREVIEW SHOWS THE **SAVED** WORDING, AND SAYS SO.
     *
     * Rendering a live preview would mean posting the draft on every keystroke —
     * and the only route that renders a draft is the one that WRITES it. A
     * config screen that silently saves as you type is a worse trade than a
     * preview that lags by one button press, so editing simply marks the preview
     * stale and the banner above it admits that.
     */
    const set = <K extends keyof DoorTextConfigDto>(key: K, value: DoorTextConfigDto[K]) => {
        setConfig(c => (c ? { ...c, [key]: value } : c));
        setData(d => (d ? { ...d, previewDirty: true } : d));
        setSaved(false);
    };

    async function save() {
        if (!config) return;
        setSaving(true);
        setError(null);
        try {
            const res = await saveDoorTextConfig(await getToken(), {
                facilityCode,
                includeDoor: config.includeDoor,
                includeAppointment: config.includeAppointment,
                includeTruckNumber: config.includeTruckNumber,
                includeFacility: config.includeFacility,
                includeSchedule: config.includeSchedule,
                includeQueue: config.includeQueue,
                greetingTemplate: config.greetingTemplate,
                doorLineTemplate: config.doorLineTemplate,
                noDoorLineTemplate: config.noDoorLineTemplate,
                customText: config.customText,
                paperworkTemplate: config.paperworkTemplate,
                enablePaperworkText: config.enablePaperworkText,
                reminderTemplate: config.reminderTemplate,
            });
            setConfig(res.config);
            setData(d => (d ? { ...d, ...res, previewDirty: false } : d));
            setSaved(true);
        } catch (err) {
            setError(err instanceof ApiCallError ? err.details.error : 'Could not save.');
        } finally {
            setSaving(false);
        }
    }

    if (!config || !data) {
        return <div className="p-3 text-sm text-muted-foreground">{loading ? 'Loading…' : (error ?? '')}</div>;
    }

    const tokens = data.tokens ?? [];

    return (
        <div className="space-y-4 p-1">
            <div className="flex items-end gap-3">
                <div className="space-y-1">
                    <Label htmlFor="dt-facility">Facility</Label>
                    <select
                        id="dt-facility"
                        value={facilityCode}
                        onChange={(e) => setFacilityCode(e.target.value)}
                        className="h-9 rounded-md border bg-background px-2 text-sm"
                    >
                        {facilities.map(f => (
                            <option key={f.FacilityCode} value={f.FacilityCode}>
                                {f.DisplayName ? `${f.FacilityCode} — ${f.DisplayName}` : f.FacilityCode}
                            </option>
                        ))}
                    </select>
                </div>
                {config.isDefaultShipped && (
                    <div className="pb-2 text-xs text-amber-700">
                        This facility has no wording of its own yet — these are the shipped
                        words. Press Save to make them this site&apos;s.
                    </div>
                )}
            </div>

            {/* ---- what goes in ------------------------------------------- */}
            <section className="space-y-2 rounded border p-3">
                <h3 className="text-sm font-medium">What the text includes</h3>
                {/* ⛔ Stated rather than implied, because somebody will look for
                    a way to move the door line and should know why there isn't one. */}
                <div className="text-[11px] text-muted-foreground">
                    The door always comes first and the detail after — that order is fixed,
                    so a driver glancing at his phone sees the door without scrolling.
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 pt-1">
                    {TOGGLES.map(t => (
                        <label key={String(t.key)} className="flex items-start gap-2 text-sm">
                            <input
                                type="checkbox"
                                className="mt-1"
                                checked={config[t.key] as boolean}
                                onChange={(e) => set(t.key, e.target.checked as never)}
                            />
                            <span>
                                {t.label}
                                <span className="block text-[11px] text-muted-foreground">{t.hint}</span>
                            </span>
                        </label>
                    ))}
                </div>
                {config.includeQueue && (
                    // ⛔ The promise this feature makes, said to the person enabling it.
                    <div className="rounded border border-slate-200 bg-slate-50 px-2 py-1.5 text-[11px] text-slate-700">
                        This reports <strong>facts only</strong> — &ldquo;2 trucks ahead of you&rdquo;,
                        &ldquo;the truck at the door has been unloading for 25 min&rdquo;. It never
                        estimates a wait, because a driver told &ldquo;about 20 minutes&rdquo; who waits
                        an hour stops believing anything else you send him.
                    </div>
                )}
            </section>

            {/* ---- the words ---------------------------------------------- */}
            <section className="space-y-2 rounded border p-3">
                <h3 className="text-sm font-medium">The words</h3>
                <div className="flex flex-wrap gap-1">
                    {tokens.map(tk => (
                        <span key={tk} className="rounded border bg-muted px-1.5 py-0.5 font-mono text-[10px]">
                            {'{' + tk + '}'}
                        </span>
                    ))}
                </div>

                <div className="space-y-1">
                    <Label htmlFor="dt-greet">Greeting</Label>
                    <Input id="dt-greet" className="font-mono text-xs" value={config.greetingTemplate}
                        onChange={(e) => set('greetingTemplate', e.target.value)} />
                </div>
                <div className="space-y-1">
                    <Label htmlFor="dt-door">When a door IS assigned</Label>
                    <Input id="dt-door" className="font-mono text-xs" value={config.doorLineTemplate}
                        onChange={(e) => set('doorLineTemplate', e.target.value)} />
                </div>
                <div className="space-y-1">
                    <Label htmlFor="dt-nodoor">When there is NO door yet</Label>
                    <Input id="dt-nodoor" className="font-mono text-xs" value={config.noDoorLineTemplate}
                        onChange={(e) => set('noDoorLineTemplate', e.target.value)} />
                    {/* ⛔ THE COMMITMENT. Worth saying out loud to whoever is
                        rewording it, because dropping the promise silently turns a
                        "we'll text you" into a driver waiting for nothing. */}
                    <div className="text-[11px] text-amber-700">
                        Keep the promise of a second text in this one. A scheduler sending it is
                        committing to follow up once the door is decided.
                    </div>
                </div>
                <div className="space-y-1">
                    <Label htmlFor="dt-reminder">The day-before reminder</Label>
                    <Input id="dt-reminder" className="font-mono text-xs" value={config.reminderTemplate}
                        onChange={(e) => set('reminderTemplate', e.target.value)} />
                    {/* ⛔ SAID PLAINLY, because this is the one message where the
                        toggles above do nothing. A reminder gets no detail line —
                        the sentence itself names the site and the time, and
                        appending them again would say everything twice inside a
                        160-character budget. A toggle that silently has no effect
                        is worse than one that is absent. */}
                    <div className="text-[11px] text-muted-foreground">
                        Sent automatically by the system, ahead of the appointment — switch it on
                        per site under <span className="font-medium">Automated Texts</span>, where the
                        lead time and quiet hours live. This sentence stands alone: the tick-boxes
                        above do not apply to it, so use{' '}
                        <code className="font-mono">{'{facility}'}</code>,{' '}
                        <code className="font-mono">{'{when}'}</code> and{' '}
                        <code className="font-mono">{'{appointment}'}</code> to say what it needs.
                    </div>
                    {/* ⚠️ THE TRAP WORTH NAMING. The lead time is configurable from 0
                        to 168 hours, so a word that is true at 24h is a lie at 4h
                        or at a week — and a driver told "tomorrow" who arrives a
                        day late has been misled by us, not by himself. */}
                    <div className="text-[11px] text-amber-700">
                        Avoid the word &ldquo;tomorrow&rdquo;. The lead time is per site and can be
                        anything from a few hours to a week, so {'{when}'} is the only thing that is
                        always true. It carries no door either — hours ahead, the door is likely to
                        change, and a driver at a stale door blocks a live one.
                    </div>
                </div>
                <div className="space-y-1">
                    <Label htmlFor="dt-paper">When the truck is finished</Label>
                    <Input id="dt-paper" className="font-mono text-xs" value={config.paperworkTemplate}
                        onChange={(e) => set('paperworkTemplate', e.target.value)} />
                    <label className="flex items-center gap-2 pt-0.5 text-[11px] text-muted-foreground">
                        <input
                            type="checkbox"
                            checked={config.enablePaperworkText}
                            onChange={(e) => set('enablePaperworkText', e.target.checked)}
                        />
                        Offer the &ldquo;paperwork ready&rdquo; button at this site
                    </label>
                    {/* ⛔ Said to whoever turns it on. Nothing in the system knows
                        the truck is finished — a human decides that by looking at
                        it, and a driver sent to an office for papers that are not
                        ready learns to ignore the next one. */}
                    <div className="text-[11px] text-muted-foreground">
                        Sent by hand, never automatically — somebody has to decide the work is
                        actually done. It carries no door and no queue: his truck is finished,
                        so neither is his problem any more.
                    </div>
                </div>

                <div className="space-y-1">
                    <Label htmlFor="dt-custom">Your own line (optional)</Label>
                    <Input id="dt-custom" className="font-mono text-xs" value={config.customText ?? ''}
                        placeholder="e.g. Check in at the guard shack before backing in"
                        onChange={(e) => set('customText', e.target.value || null)} />
                    <div className="text-[11px] text-muted-foreground">
                        Added to every dock-door text from this site. Turn every box above off and
                        put your whole message here if you would rather write it yourself.
                    </div>
                </div>
            </section>

            {/* ---- previews ------------------------------------------------ */}
            <section className="space-y-2 rounded border p-3">
                <h3 className="text-sm font-medium">
                    What a driver gets
                    {data.previewDirty && (
                        // ⚠️ SAID PLAINLY. A preview that silently lags the fields
                        // above is worse than one that admits it is stale.
                        <span className="ml-2 text-[11px] font-normal text-amber-700">
                            showing the saved wording — press Save to update
                        </span>
                    )}
                </h3>
                <div className="space-y-1">
                    <div className="text-[11px] text-muted-foreground">With a door assigned</div>
                    <pre className="whitespace-pre-wrap rounded border bg-muted/50 px-2 py-1.5 font-sans text-xs">
{data.preview}
                    </pre>
                    {data.previewSegments > 1 && (
                        <div className="text-[11px] text-amber-700">
                            {data.previewSegments} text messages — it may arrive in pieces.
                        </div>
                    )}
                </div>
                <div className="space-y-1">
                    <div className="text-[11px] text-muted-foreground">
                        Before a door is assigned — the one a waiting driver usually gets
                    </div>
                    <pre className="whitespace-pre-wrap rounded border bg-muted/50 px-2 py-1.5 font-sans text-xs">
{data.previewNoDoor}
                    </pre>
                    {data.previewNoDoorSegments > 1 && (
                        <div className="text-[11px] text-amber-700">
                            {data.previewNoDoorSegments} text messages.
                        </div>
                    )}
                </div>
                {config.enablePaperworkText && (
                    <div className="space-y-1">
                        <div className="text-[11px] text-muted-foreground">
                            When the truck is finished
                        </div>
                        <pre className="whitespace-pre-wrap rounded border bg-muted/50 px-2 py-1.5 font-sans text-xs">
{data.previewPaperwork}
                        </pre>
                        {data.previewPaperworkSegments > 1 && (
                            <div className="text-[11px] text-amber-700">
                                {data.previewPaperworkSegments} text messages.
                            </div>
                        )}
                    </div>
                )}
                {/* ⛔ ALWAYS SHOWN, unlike the paperwork preview above which is
                    hidden when that button is switched off. A reminder is sent by
                    a TIMER: nobody sees it go, so this screen is the only place
                    anybody ever reads it before a driver does. Hiding it behind
                    "reminders are enabled somewhere" would mean the message most
                    likely to go out unreviewed is the one nobody reviewed. */}
                <div className="space-y-1">
                    <div className="text-[11px] text-muted-foreground">
                        The automatic reminder, ahead of the appointment
                    </div>
                    <pre className="whitespace-pre-wrap rounded border bg-muted/50 px-2 py-1.5 font-sans text-xs">
{data.previewReminder}
                    </pre>
                    {data.previewReminderSegments > 1 && (
                        <div className="text-[11px] text-amber-700">
                            {data.previewReminderSegments} text messages — and this one is sent
                            automatically, so nobody will notice it arriving in pieces.
                        </div>
                    )}
                </div>
                {data.previewUnicode && (
                    // ⚠️ THE SILENT COST. One smart quote pasted from Word drops the
                    // per-message limit from 160 characters to 70 — for every text
                    // this site ever sends.
                    <div className="rounded border border-amber-400 bg-amber-50 px-2 py-1 text-[11px] text-amber-900">
                        A special character (a curly quote or dash, often pasted from Word) has
                        halved the length limit for every message from this site. Retyping the
                        punctuation by hand usually fixes it.
                    </div>
                )}
            </section>

            {error && (
                <div className="rounded border border-destructive/40 bg-destructive/5 px-2 py-1 text-sm text-destructive">
                    {error}
                </div>
            )}

            <div className="flex items-center gap-3">
                <Button size="sm" onClick={() => void save()} disabled={saving}>
                    {saving ? 'Saving…' : 'Save'}
                </Button>
                {saved && <span className="text-xs text-emerald-700">Saved.</span>}
                <button
                    type="button"
                    className="text-xs underline text-muted-foreground"
                    onClick={() => { if (data.shipped) { setConfig({ ...config, ...data.shipped }); setSaved(false); } }}
                >
                    Reset to the shipped wording
                </button>
            </div>
        </div>
    );
}
